import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { StatusIcon } from '@/components/ui/StatusIcon';
import { Badge } from '@/components/ui/Badge';
import { Bell, ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toNumber } from '@/lib/number';
import type { PondOverview, AlertThresholds } from '@/types/api';
import { Term } from '@/components/ui/Term';
import { DayReadingsTable } from './DayReadingsTable';

interface PondCardProps {
  pond: PondOverview;
  alertThresholds?: AlertThresholds | null;
  onClick?: () => void;
}

export function PondCard({ pond, alertThresholds, onClick }: PondCardProps) {
  const isActive = pond.status === 'active';
  const cycle = pond.active_cycle;
  const reading = cycle?.latest_reading;

  // Determine overall status for the card
  const alertCount = toNumber(pond.alert_count, 0);
  const getStatus = (): 'ok' | 'warning' | 'critical' | 'pending' => {
    if (alertCount > 0) return 'critical';
    if (!isActive) return 'pending';
    if (reading?.has_alert) return 'warning';
    return 'ok';
  };

  const status = getStatus();

  // Status-based border accent
  const borderAccent = {
    ok: 'hover:border-l-status-ok',
    warning: 'hover:border-l-status-warning',
    critical: 'border-l-status-critical',
    pending: 'hover:border-l-gray-400',
  };

  return (
    <Card
      interactive
      className={cn(
        'border-l-4 border-l-transparent transition-all duration-200 cursor-pointer',
        borderAccent[status],
        status === 'critical' && 'border-l-status-critical shadow-status-critical/10',
        !isActive && 'opacity-75'
      )}
      onClick={onClick}
    >
      <Card.Body className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <StatusIcon status={status} size="md" />
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{pond.name}</h3>
              {isActive && cycle ? (
                <p className="text-sm text-gray-500">
                  <Term term="DOC" /> <span className="font-medium text-gray-700">{cycle.doc}</span>
                </p>
              ) : (
                <p className="text-sm text-gray-400 capitalize">{pond.status}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {alertCount > 0 && (
              <Badge variant="critical">
                <Bell className="h-3 w-3 mr-1" />
                {alertCount}
              </Badge>
            )}
            <Link
              to="/ponds/$pondId"
              params={{ pondId: pond.id }}
              onClick={(e) => e.stopPropagation()}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* The newest reading day by round (#392) */}
        {isActive && cycle?.day_readings && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <DayReadingsTable day={cycle.day_readings} alertThresholds={alertThresholds} />
          </div>
        )}

        {/* Empty state for inactive ponds */}
        {!isActive && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-400 italic">
              {pond.status === 'preparing' ? 'Preparing' : 'No active cycle'}
            </p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

